import React, { useEffect, useState, useRef } from 'react';

export default function Cursor() {
  const [isHovering, setIsHovering] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Use refs for positions to avoid re-renders
  const cursorRef = useRef(null);
  const followerRef = useRef(null);
  const mousePosition = useRef({ x: 0, y: 0 });
  const followerPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    // Check if device supports hover (desktop)
    const hasHover = window.matchMedia('(hover: hover)').matches;
    if (!hasHover) return;

    setIsVisible(true);

    const updateCursor = (e) => {
      mousePosition.current = { x: e.clientX, y: e.clientY };

      // Update cursor dot immediately for instant response
      if (cursorRef.current) {
        cursorRef.current.style.left = `${e.clientX}px`;
        cursorRef.current.style.top = `${e.clientY}px`;
      }
    };

    const updateFollower = () => {
      // Smooth follower animation with increased smoothing factor
      followerPosition.current.x += (mousePosition.current.x - followerPosition.current.x) * 0.2;
      followerPosition.current.y += (mousePosition.current.y - followerPosition.current.y) * 0.2;

      if (followerRef.current) {
        followerRef.current.style.left = `${followerPosition.current.x}px`;
        followerRef.current.style.top = `${followerPosition.current.y}px`;
      }
    };

    const handleMouseEnter = (e) => {
      const target = e.target;
      const isInteractive = target.matches('a, button, input, textarea, select, [role="button"], .cursor-hover');
      setIsHovering(isInteractive);
    };

    const handleMouseLeave = () => {
      setIsHovering(false);
    };

    // Mouse move listener
    document.addEventListener('mousemove', updateCursor);

    // Follower animation with requestAnimationFrame for smoother performance
    let animationFrameId;
    const animate = () => {
      updateFollower();
      animationFrameId = requestAnimationFrame(animate);
    };
    animationFrameId = requestAnimationFrame(animate);

    // Interactive element listeners
    document.addEventListener('mouseover', handleMouseEnter);
    document.addEventListener('mouseout', handleMouseLeave);

    return () => {
      document.removeEventListener('mousemove', updateCursor);
      document.removeEventListener('mouseover', handleMouseEnter);
      document.removeEventListener('mouseout', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []); // Empty dependency array - setup once

  if (!isVisible) return null;

  return (
    <>
      {/* Main cursor dot */}
      <div
        ref={cursorRef}
        className={`cursor ${isHovering ? 'cursor-hover' : ''}`}
        style={{
          transform: `translate3d(-50%, -50%, 0) ${isHovering ? 'scale(1.5)' : 'scale(1)'}`,
          background: isHovering ? 'rgba(212, 175, 55, 1)' : 'rgba(212, 175, 55, 0.8)',
          mixBlendMode: isHovering ? 'normal' : 'difference'
        }}
      />

      {/* Follower ring */}
      <div
        ref={followerRef}
        className={`cursor-follower ${isHovering ? 'cursor-follower-hover' : ''}`}
        style={{
          transform: `translate3d(-50%, -50%, 0) ${isHovering ? 'scale(1.5)' : 'scale(1)'}`,
          borderColor: isHovering ? 'rgba(212, 175, 55, 0.6)' : 'rgba(212, 175, 55, 0.3)'
        }}
      />
    </>
  );
}
